import SEO from '../../components/common/SEO';
import { ExternalLink } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

interface CreditItem {
  name: string;
  author: string;
  authorUrl: string;
  sourceUrl: string;
  license: string;
  licenseUrl: string;
  description: string;
}

const credits: CreditItem[] = [
  {
    name: 'Golden Ring 3D Model',
    author: 'Busanello',
    authorUrl: 'https://sketchfab.com/busanello',
    sourceUrl: 'https://sketchfab.com/3d-models/free-ring-f08e7362b9cc4cacaa5e99a323f8de05',
    license: 'CC-BY-4.0',
    licenseUrl: 'http://creativecommons.org/licenses/by/4.0/',
    description: 'The golden ring used in the RUMIO logo and as the in-game currency symbol.',
  },
  {
    name: 'Theater Curtains 3D Model',
    author: 'daedaljs',
    authorUrl: 'https://sketchfab.com/daedalJS',
    sourceUrl: 'https://sketchfab.com/3d-models/curtains-20a04e945be84b7fab957321474f3b73',
    license: 'CC-BY-4.0',
    licenseUrl: 'http://creativecommons.org/licenses/by/4.0/',
    description: 'Animated curtains used for the mystery deal reveal animation on the Deals page.',
  },
];

export default function Credits() {
  const { t } = useLanguage();

  return (
    <>
      <SEO page="credits" />
      <div className="py-16 bg-white">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('credits.title', 'Credits & Attributions')}
          </h1>
          <p className="text-gray-600 mb-8">
            {t('credits.intro', 'RUMIO uses the following third-party assets. We\'re grateful to the creators who have made their work available for use.')}
          </p>

          <div className="space-y-6">
            {credits.map((credit) => (
              <div
                key={credit.name}
                className="bg-gray-50 rounded-lg p-6 border border-gray-200"
              >
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  {credit.name}
                </h2>
                <p className="text-gray-600 mb-4">{credit.description}</p>

                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="font-medium text-gray-500">{t('credits.creator', 'Creator')}</dt>
                    <dd>
                      <a
                        href={credit.authorUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                      >
                        {credit.author}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt className="font-medium text-gray-500">{t('credits.license', 'License')}</dt>
                    <dd>
                      <a
                        href={credit.licenseUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1"
                      >
                        {credit.license}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="font-medium text-gray-500">{t('credits.source', 'Source')}</dt>
                    <dd>
                      <a
                        href={credit.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 break-all"
                      >
                        {credit.sourceUrl}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    </dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              {t('credits.openSourceTitle', 'Open Source Libraries')}
            </h2>
            <p className="text-gray-600">
              {t('credits.openSourceDescription', 'RUMIO is built with many amazing open source libraries. For a full list of dependencies and their licenses, please see our')}{' '}
              <a
                href="https://github.com/rumio"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700"
              >
                {t('credits.githubRepo', 'GitHub repository')}
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
